"use client";

import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useMemo } from "react";
import SalifyLink from "src/lib/components/link/salify-link";
import { toAssetImages } from "src/lib/utils";
import {
  LayoutViewMode,
  ResponsivePropsConfig,
  resolveResponsiveProps,
  splitDesktopAndResponsive,
} from "src/lib/utils/responsive-props";

export type CardGridButton = {
  text: string;
  link: string;
};

export type CardGridItem = {
  thumbnail?: string;
  title: string;
  description?: string;
};

type CardGridViewportData = {
  backgroundImage?: string;
  title?: string;
  heading1?: string;
  items?: CardGridItem[];
  ctaDescription?: string;
  button?: CardGridButton;
};

export interface VariantProps extends CardGridViewportData {
  responsive?: ResponsivePropsConfig<CardGridViewportData>;
}

const ACCENT = "#FFC107";
const CARD_BORDER = "#e8e8e8";

const useLayoutViewMode = (): LayoutViewMode => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("tablet"));
  const isTablet = useMediaQuery(theme.breakpoints.between("tablet", "laptop"));
  if (isMobile) return "mobile";
  if (isTablet) return "tablet";
  return "desktop";
};

const ItemCard = ({ item }: { item: CardGridItem }) => {
  const thumbUrl = item.thumbnail?.trim() ? toAssetImages(item.thumbnail as string) : "";

  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 2,
        border: `1px solid ${CARD_BORDER}`,
        bgcolor: "#fff",
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: 1.5,
          p: { xs: 2.5, laptop: 3 },
          minHeight: { xs: 220, laptop: 260 },
        }}
      >
        {thumbUrl ? (
          <Box
            component="img"
            src={thumbUrl}
            alt={item.title}
            loading="lazy"
            sx={{
              width: { xs: 56, laptop: 64 },
              height: { xs: 56, laptop: 64 },
              objectFit: "contain",
            }}
          />
        ) : (
          <Box
            sx={{
              width: { xs: 56, laptop: 64 },
              height: { xs: 56, laptop: 64 },
              borderRadius: 1,
              bgcolor: "grey.100",
            }}
          />
        )}
        <Typography
          component="h3"
          sx={{
            fontWeight: 700,
            fontSize: { xs: 15, laptop: 17 },
            lineHeight: 1.35,
            color: "text.primary",
          }}
        >
          {item.title}
        </Typography>
        {item.description ? (
          <Typography
            sx={{
              fontSize: { xs: 13, laptop: 14 },
              lineHeight: 1.65,
              color: "text.secondary",
            }}
          >
            {item.description}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
};

const CtaCard = ({ description, button }: { description?: string; button?: CardGridButton }) => (
  <Card
    elevation={0}
    sx={{
      height: "100%",
      borderRadius: 2,
      border: `1px solid ${CARD_BORDER}`,
      bgcolor: "#fff",
    }}
  >
    <CardContent
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: 2,
        p: { xs: 2.5, laptop: 3 },
        minHeight: { xs: 220, laptop: 260 },
      }}
    >
      {description ? (
        <Typography
          sx={{
            fontSize: { xs: 14, laptop: 15 },
            lineHeight: 1.6,
            color: "text.secondary",
            maxWidth: 280,
          }}
        >
          {description}
        </Typography>
      ) : null}
      {button?.text ? (
        <SalifyLink href={button.link || "#"}>
          <Button
            variant="contained"
            sx={{
              bgcolor: ACCENT,
              color: "#111",
              fontWeight: 600,
              px: 3,
              py: 1.1,
              borderRadius: 1,
              boxShadow: "none",
              whiteSpace: "nowrap",
              "&:hover": { bgcolor: "#e6ad06", boxShadow: "none" },
            }}
          >
            {button.text}
          </Button>
        </SalifyLink>
      ) : null}
    </CardContent>
  </Card>
);

const CardGridDefaultV2 = (props: VariantProps) => {
  const viewMode = useLayoutViewMode();
  const { desktop, responsive } = splitDesktopAndResponsive(
    props as unknown as Record<string, unknown>,
  );

  const data = useMemo((): CardGridViewportData => {
    const resolved = resolveResponsiveProps(
      {
        backgroundImage: (desktop.backgroundImage as string) || "",
        title: (desktop.title as string) || "",
        heading1: (desktop.heading1 as string) || "",
        items: (desktop.items as CardGridItem[]) || [],
        ctaDescription: (desktop.ctaDescription as string) || "",
        button: desktop.button as CardGridButton | undefined,
      },
      responsive,
      viewMode,
      ["backgroundImage", "title", "heading1", "items", "ctaDescription", "button"],
    );

    return {
      backgroundImage: String(resolved.backgroundImage || ""),
      title: String(resolved.title || ""),
      heading1: String(resolved.heading1 || ""),
      items: Array.isArray(resolved.items) ? resolved.items : [],
      ctaDescription: String(resolved.ctaDescription || ""),
      button: resolved.button as CardGridButton | undefined,
    };
  }, [desktop, responsive, viewMode]);

  const backgroundUrl = data.backgroundImage?.trim()
    ? toAssetImages(data.backgroundImage as string)
    : "";
  const items = data.items?.filter((item) => item?.title?.trim()) || [];
  const showCtaCard = Boolean(data.ctaDescription?.trim() || data.button?.text);

  return (
    <Box
      component="section"
      id="card-grid"
      sx={{
        position: "relative",
        py: { xs: 5, laptop: 7 },
        bgcolor: "#f5f5f5",
        backgroundImage: backgroundUrl ? `url(${backgroundUrl})` : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {backgroundUrl ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(255,255,255,0.92)",
          }}
        />
      ) : null}

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{ textAlign: "center", mb: { xs: 4, laptop: 5 } }}>
          {data.title ? (
            <Typography
              sx={{
                color: "text.secondary",
                fontSize: { xs: 14, laptop: 15 },
                mb: 1,
              }}
            >
              {data.title}
            </Typography>
          ) : null}
          {data.heading1 ? (
            <Typography
              component="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: 22, tablet: 26, laptop: 30 },
                lineHeight: 1.25,
                color: "text.primary",
                textTransform: "uppercase",
                letterSpacing: "0.02em",
              }}
            >
              {data.heading1}
            </Typography>
          ) : null}
        </Box>

        <Grid container spacing={{ xs: 2, laptop: 2.5 }}>
          {items.map((item, index) => (
            <Grid item xs={12} tablet={6} laptop={3} key={`${item.title}-${index}`}>
              <ItemCard item={item} />
            </Grid>
          ))}
          {showCtaCard ? (
            <Grid item xs={12} tablet={6} laptop={3}>
              <CtaCard description={data.ctaDescription} button={data.button} />
            </Grid>
          ) : null}
        </Grid>
      </Container>
    </Box>
  );
};

export default CardGridDefaultV2;
